import { useState, useEffect } from "react";
import { Wallet, Calculator, TrendingUp, PieChart, Database, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import RoiCharts from "./RoiCharts";

export default function BankrollManager({ bankroll, setBankroll, kellyFraction, setKellyFraction }) {
  const [roi, setRoi] = useState(null);
  const [betHistory, setBetHistory] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [inputVal, setInputVal] = useState(bankroll.toString());
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    api.getRoi().then(setRoi).catch(() => {});
    api.getCacheStats().then(setCacheStats).catch(() => {});
    setLoadingHistory(true);
    api.getBetHistory(50).then((data) => {
      setBetHistory(data);
    }).catch(() => {}).finally(() => setLoadingHistory(false));
  }, []);

  const handleUpdate = () => {
    const val = parseFloat(inputVal);
    if (val >= 1 && val <= 1000000) {
      setBankroll(val);
    }
  };

  const maxStake = (bankroll * 0.05).toFixed(2);
  const unitStake = (bankroll * 0.02).toFixed(2);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="bankroll-manager">
      <h2 className="font-heading text-2xl font-black uppercase tracking-tight">
        Gestao de Banca
      </h2>

      {/* Top Row: Bankroll + Kelly + Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Bankroll Input */}
        <div className="bg-surface border border-line rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-4 h-4 text-volt" />
            <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-txt-secondary">
              A Tua Banca
            </h3>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                data-testid="bankroll-input"
                type="number"
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onBlur={handleUpdate}
                onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
                className="w-full bg-obsidian border border-line rounded-sm px-4 py-3 font-mono text-xl font-bold text-white focus:border-volt focus:outline-none transition-colors"
                min="1"
                max="1000000"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-txt-secondary text-sm">EUR</span>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-txt-secondary uppercase tracking-widest">Kelly Fracao</span>
              <span className="font-mono text-sm text-volt">{(kellyFraction * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="kelly-slider"
              type="range"
              min="10"
              max="100"
              value={kellyFraction * 100}
              onChange={(e) => setKellyFraction(parseInt(e.target.value) / 100)}
              className="w-full accent-volt h-1.5 bg-obsidian rounded-full appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-txt-secondary">
              <span>Conservador (10%)</span>
              <span>Full Kelly (100%)</span>
            </div>
          </div>
        </div>

        {/* Stake Info */}
        <div className="bg-surface border border-line rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-4 h-4 text-volt" />
            <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-txt-secondary">
              Stakes Kelly
            </h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-txt-secondary mb-1">Stake Maximo (5%)</p>
              <p className="font-mono text-3xl font-bold text-white">{maxStake}<span className="text-sm text-txt-secondary ml-1">EUR</span></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-txt-secondary mb-1">Unidade (2%)</p>
              <p className="font-mono text-3xl font-bold text-volt">{unitStake}<span className="text-sm text-txt-secondary ml-1">EUR</span></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-txt-secondary mb-1">Kelly Fracao</p>
              <p className="font-mono text-xl font-bold text-azure">{(kellyFraction * 100).toFixed(0)}%<span className="text-sm text-txt-secondary ml-1">do Kelly Total</span></p>
            </div>
          </div>
        </div>

        {/* ROI Summary Card */}
        <div className="bg-surface border border-line rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-4 h-4 text-volt" />
            <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-txt-secondary">
              ROI Resumo
            </h3>
          </div>
          {roi && roi.total_bets > 0 ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-xs text-txt-secondary">Total Apostas</span>
                <span className="font-mono text-sm font-bold">{roi.total_bets}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-txt-secondary">Ganhas / Perdidas</span>
                <span className="font-mono text-sm">
                  <span className="text-volt">{roi.wins}</span> / <span className="text-danger">{roi.losses}</span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-txt-secondary">Taxa Acerto</span>
                <span className={`font-mono text-sm font-bold ${roi.win_rate >= 50 ? "text-volt" : "text-danger"}`}>
                  {roi.win_rate}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-txt-secondary">Lucro Total</span>
                <span className={`font-mono text-sm font-bold ${roi.total_profit >= 0 ? "text-volt" : "text-danger"}`}>
                  {roi.total_profit > 0 ? "+" : ""}{roi.total_profit} EUR
                </span>
              </div>
              <div className="border-t border-line pt-2 flex justify-between">
                <span className="text-xs text-txt-secondary font-bold">ROI</span>
                <span className={`font-mono text-2xl font-bold ${roi.roi >= 0 ? "text-volt" : "text-danger"}`}>
                  {roi.roi > 0 ? "+" : ""}{roi.roi}%
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <TrendingUp className="w-8 h-8 text-txt-secondary/30 mx-auto mb-2" />
              <p className="text-xs text-txt-secondary">
                Sem dados de apostas registadas.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ROI Charts Section */}
      {loadingHistory ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-volt animate-spin" />
        </div>
      ) : (
        betHistory && (
          <RoiCharts
            bets={betHistory.bets}
            summary={betHistory.summary}
          />
        )
      )}

      {/* Cache Stats */}
      {cacheStats && (
        <div className="bg-surface border border-line rounded-sm p-4 flex items-center gap-4 text-xs text-txt-secondary">
          <Database className="w-4 h-4 text-azure" />
          <span>Cache: {cacheStats.cached_api_calls} chamadas guardadas</span>
          <span>|</span>
          <span>{cacheStats.total_matches} jogos na BD</span>
          <span>|</span>
          <span>{cacheStats.matches_with_stats} com estatisticas detalhadas</span>
        </div>
      )}

      {/* Ad Banner Simulation */}
      <div
        className="border border-dashed border-txt-secondary/30 rounded-sm py-3 flex items-center justify-center"
        data-testid="admob-banner"
      >
        <span className="text-txt-secondary/40 text-xs uppercase tracking-[0.3em] font-bold">
          Advertisement
        </span>
      </div>
    </div>
  );
}
